import ReservationSidebar from '../Commun/ReservationSidebar';
import ReservationHeader from '../Commun/ReservationHeader';
import { useEffect, useState } from 'react';
import { Property, ReserBusinessSource } from '../../Services/Service';
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { fetchBusinessPropertyYearlyReport, fetchBusinessYearlyReport } from '../../Redux/Slices/BusinessSourceReportSlice';

export const BusinessYearlyReport = () => {
    const dispatch = useDispatch();

    const [propertyList, setPropertyList] = useState([]);
    const [selectedProperty, setSelectedProperty] = useState([]);
    const [selectedYear, setSelectedYear] = useState([]);
    const [sources, setSources] = useState([]);
    const [selectedSource, setSelectedSource] = useState([]);
    const [reportType, setReportType] = useState("source");

    const loading = useSelector(state => state.businessSourceReport.loading);

    // 🔹 Fetch Sources
    useEffect(() => {
        const fetchBusinessSource = async () => {
            try {
                const data = await ReserBusinessSource(333333);
                if (Array.isArray(data)) setSources(data);
            } catch (error) {
                console.log(error);
            }
        };
        fetchBusinessSource();
    }, []);

    // 🔹 Default values
    useEffect(() => {
        fetchProperty();

        const now = new Date();

        setSelectedYear([{
            value: now.getFullYear(),
            label: now.getFullYear().toString()
        }]);
    }, []);

    const fetchProperty = async () => {
        const data = await Property();
        setPropertyList(data);

        setSelectedProperty(
            data.map(p => ({
                value: p.PropertyID,
                label: p.PropertyName
            }))
        );
    };

    // 🔹 Options
    const propertyOptions = propertyList.map(p => ({
        value: p.PropertyID,
        label: p.PropertyName
    }));

    const yearOptions = Array.from({ length: 10 }, (_, i) => {
        const y = new Date().getFullYear() - i;
        return { value: y, label: y.toString() };
    });

    const sourceOptions = sources
        ?.filter(s => s.BONGroupCode && s.BONBusinessSource)
        .map(s => ({
            value: s.BONGroupCode,
            label: s.BONBusinessSource
        }));

    // 🔹 Handlers
    const handlePropertyChange = v => setSelectedProperty(v || []);
    const handleYearChange = v => setSelectedYear(v || []);
    const handleSourceChange = v => setSelectedSource(v || []);

    // 🔹 Generate Yearly Table
    const generateYearlyPage = (doc, data, yearObj, today, isFirstPage, selectedProperty) => {

        const pageWidth = doc.internal.pageSize.getWidth();

        if (!isFirstPage) doc.addPage();

        const year = yearObj.value;

        // 🔹 Title
        doc.setFontSize(16);
        doc.text("Source Yearly Report", pageWidth / 2, 10, { align: "center" });

        doc.setFontSize(10);
        doc.text(`Generated: ${today}`, pageWidth - 10, 10, { align: "right" });

        // =========================
        // 🔹 PROPERTY BOX (SAME AS ABOVE)
        // =========================

        let propertyList = [];

        if (selectedProperty.length === propertyOptions.length) {
            propertyList = ["All Property"];
        } else {
            propertyList = selectedProperty.map(p => p.label);
        }

        const startX = 12;
        const boxY = 14;

        const label = "Property:";
        const labelWidth = doc.getTextWidth(label);

        const textStartX = startX + labelWidth + 6;

        const lineHeight = 5;

        const rowsProp = [];
        let currentLine = "";
        let currentWidth = 0;

        const maxWidth = pageWidth - textStartX - 12;

        propertyList.forEach((name, index) => {

            const isLast = index === propertyList.length - 1;

            const separator = isLast ? "" : "  |  ";
            const text = name + separator;

            const textWidth = doc.getTextWidth(text);

            if (currentWidth + textWidth <= maxWidth) {
                currentLine += text;
                currentWidth += textWidth;
            } else {
                rowsProp.push(currentLine.trim());
                currentLine = text;
                currentWidth = textWidth;
            }
        });

        if (currentLine) {
            rowsProp.push(currentLine.trim());
        }

        const boxHeight = (rowsProp.length * lineHeight) + 1.5;

        doc.setDrawColor(180, 180, 180);
        doc.setLineWidth(0.5);
        doc.rect(10, boxY, pageWidth - 20, boxHeight);

        doc.setFont(undefined, "bold");
        doc.setFontSize(10);
        doc.text("Property:", startX, boxY + 4);

        doc.setFont(undefined, "normal");

        let y = boxY + 4;

        rowsProp.forEach(line => {
            doc.text(line, textStartX, y);
            y += lineHeight;
        });

        const startY = y + 3;

        // =========================
        // 🔢 NUMBER FORMAT
        // =========================
        const formatNumber = (num) => {
            if (!num) return "0";
            return Number(num).toLocaleString("en-IN");
        };

        const months = [
            "Jan", "Feb", "Mar", "Apr", "May", "Jun",
            "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
        ];

        // =========================
        // ✅ HEADER
        // =========================
        const headerRow = [
            "Business Source",
            "Type",
            ...months.map(m => `${m} ${year}`)
        ];

        // =========================
        // ✅ ROWS
        // =========================
        const rows = [];

        data.forEach(item => {

            rows.push([
                {
                    content: item.BusinessSource || "",
                    rowSpan: 2,
                    styles: {
                        valign: 'middle',
                        halign: 'left'
                    }
                },
                {
                    content: "RM NTS",
                    styles: { halign: 'left' }
                },
                ...months.map(m => formatNumber(item[`${m}_OCC`] || 0))
            ]);

            rows.push([
                {
                    content: "REV.",
                    styles: { halign: 'left' }
                },
                ...months.map(m => formatNumber(item[`${m}_REV`] || 0))
            ]);
        });

        // =========================
        // 🔹 WIDTH
        // =========================
        const totalCols = 2 + months.length;
        const totalWidth = pageWidth - 20;

        const firstColWidth = 35;
        const secondColWidth = 15;
        const otherColWidth = (totalWidth - firstColWidth - secondColWidth) / months.length;

        const columnStyles = {
            0: { cellWidth: firstColWidth, halign: 'left', valign: 'middle' },
            1: { cellWidth: secondColWidth, halign: 'center' }
        };

        for (let i = 2; i < totalCols; i++) {
            columnStyles[i] = { cellWidth: otherColWidth, halign: 'right' };
        }

        // =========================
        // 🔥 TABLE
        // =========================
        autoTable(doc, {
            startY,
            head: [headerRow],
            body: rows,
            theme: 'grid',

            styles: {
                fontSize: 7,
                cellPadding: 1.5,
                halign: 'center',
                valign: 'middle',
                lineWidth: 0.2
            },

            headStyles: {
                fillColor: [41, 128, 185],
                textColor: 255
            },

            columnStyles,
            margin: { left: 10, right: 10 }
        });
    };

    const generateYearlyPageProperty = (
        doc,
        data,
        yearObj,
        today,
        isFirstPage,
        SourceNames
    ) => {
        const pageWidth = doc.internal.pageSize.getWidth();

        if (!isFirstPage) {
            doc.addPage();
        }

        const year = yearObj.value;

        // 🔹 Title
        doc.setFontSize(16);
        doc.text("Property Source Yearly Report", pageWidth / 2, 10, { align: "center" });

        doc.setFontSize(10);
        doc.text(`Generated: ${today}`, pageWidth - 10, 10, { align: "right" });

        // ✅ SOURCE NAME SHOW
        if (SourceNames && SourceNames.length > 0) {
            doc.text(`Source: ${SourceNames.join(", ")}`, 10, 16);
        }

        const startY = SourceNames?.length ? 22 : 20;

        const months = [
            "Jan", "Feb", "Mar", "Apr", "May", "Jun",
            "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
        ];

        // ✅ FORMAT FUNCTION
        const formatNumber = (num) => {
            if (!num) return "0";
            return Number(num).toLocaleString("en-IN");
        };

        // 🔹 HEADER
        const headerRow = [
            "Property Name",
            "Type",
            ...months.map(m => `${m} ${year}`)
        ];

        // 🔹 ROWS (🔥 MAIN CHANGE)
        const rows = [];

        data.forEach(item => {

            // 🔹 RM NTS ROW
            rows.push([
                {
                    content: item.PropertyName || "",
                    rowSpan: 2,
                    styles: {
                        valign: 'middle',
                        halign: 'left'
                    }
                },
                {
                    content: "RM NTS",
                    styles: {
                        halign: 'left',
                        // light color
                    }
                },
                ...months.map(m => formatNumber(item[`${m}_OCC`] || 0))
            ]);

            // 🔹 REV ROW
            rows.push([
                {
                    content: "REV.",
                    styles: {
                        halign: 'left',
                        // light color
                    }
                },
                ...months.map(m => formatNumber(item[`${m}_REV`] || 0))
            ]);
        });

        // 🔥 WIDTH SAME LOGIC
        const totalCols = 2 + months.length;
        const totalWidth = pageWidth - 20;

        const firstColWidth = 35;
        const secondColWidth = 15;
        const otherColWidth = (totalWidth - firstColWidth - secondColWidth) / months.length;

        const columnStyles = {
            0: { cellWidth: firstColWidth, halign: 'left', valign: 'middle' },
            1: { cellWidth: secondColWidth, halign: 'center' }
        };

        for (let i = 2; i < totalCols; i++) {
            columnStyles[i] = { cellWidth: otherColWidth, halign: 'right' };
        }

        // 🔥 TABLE
        autoTable(doc, {
            startY,
            head: [headerRow],
            body: rows,
            theme: 'grid',

            styles: {
                fontSize: 7,
                cellPadding: 1.5,
                halign: 'center',
                valign: 'middle',
                lineWidth: 0.2
            },

            headStyles: {
                fillColor: [41, 128, 185],
                textColor: 255
            },

            columnStyles,
            margin: { left: 10, right: 10 }
        });
    };

    // 🔹 MAIN REPORT

    // const handleReport = async (e) => {
    //     e.preventDefault();

    //     if (!selectedProperty.length || !selectedYear.length) {
    //         alert("Please select required fields");
    //         return;
    //     }

    //     const doc = new jsPDF("landscape");
    //     const today = new Date().toLocaleDateString("en-GB", {
    //         day: "2-digit",
    //         month: "long",
    //         year: "numeric"
    //     });

    //     let isFirstPage = true;

    //     // 🔥 SOURCE REPORT
    //     if (reportType === "source") {

    //         const PID = selectedProperty.map(p => p.value);
    //         const Codes = selectedSource.map(s => s.value);

    //         for (const yearObj of selectedYear) {

    //             const payload = {
    //                 PID: `[${PID}]`,
    //                 Year: yearObj.value,
    //                 Code: Codes.length ? Codes[0] : ""
    //             };

    //             const result = await dispatch(fetchBusinessYearlyReport(payload));
    //             const data = result.payload || [];

    //             generateYearlyPage(
    //                 doc,
    //                 data,
    //                 yearObj,
    //                 today,
    //                 isFirstPage,
    //                 selectedProperty
    //             );

    //             isFirstPage = false;
    //         }

    //         doc.save("Source_Yearly_Report.pdf");
    //     }

    //     // 🔥 PROPERTY REPORT
    //     else {

    //         const PID = selectedProperty.map(p => p.value);
    //         const Codes = selectedSource.map(s => s.value);
    //         const SourceNames = selectedSource.map(s => s.label);

    //         for (const yearObj of selectedYear) {

    //             const payload = {
    //                 PID: `[${PID}]`,
    //                 Year: yearObj.value,
    //                 Code: Codes.length ? Codes[0] : ""
    //             };

    //             const result = await dispatch(fetchBusinessPropertyYearlyReport(payload));
    //             const data = result.payload || [];

    //             generateYearlyPageProperty(
    //                 doc,
    //                 data,
    //                 yearObj,
    //                 today,
    //                 isFirstPage,
    //                 SourceNames
    //             );

    //             isFirstPage = false;
    //         }

    //         doc.save("Property_Source_Yearly_Report.pdf");
    //     }
    // };

    const handleReport = async (e) => {
        e.preventDefault();

        if (!selectedProperty.length || !selectedYear.length) {
            alert("Please select required fields");
            return;
        }

        const doc = new jsPDF("landscape");

        const today = new Date().toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "long",
            year: "numeric"
        });

        let isFirstPage = true;

        const PID = selectedProperty.map(p => p.value);
        const Codes = selectedSource.map(s => s.value);
        const SourceNames = selectedSource.map(s => s.label);

        // =========================
        // 🔥 SOURCE REPORT
        // =========================
        if (reportType === "source") {

            for (const yearObj of selectedYear) {

                const payload = {
                    PID: `[${PID}]`,
                    Year: yearObj.value,

                    // ✅ FIX HERE (NO Codes[0])
                    Code: Codes.length ? `[${Codes.join(",")}]` : ""
                };

                const result = await dispatch(fetchBusinessYearlyReport(payload));
                const data = result.payload || [];

                generateYearlyPage(
                    doc,
                    data,
                    yearObj,
                    today,
                    isFirstPage,
                    selectedProperty
                );

                isFirstPage = false;
            }

            doc.save("Source_Yearly_Report.pdf");
        }

        // =========================
        // 🔥 PROPERTY REPORT
        // =========================
        else {

            for (const yearObj of selectedYear) {

                const payload = {
                    PID: `[${PID}]`,
                    Year: yearObj.value,

                    // ✅ SAME FIX HERE
                    Code: Codes.length ? `[${Codes.join(",")}]` : ""
                };

                const result = await dispatch(fetchBusinessPropertyYearlyReport(payload));
                const data = result.payload || [];

                generateYearlyPageProperty(
                    doc,
                    data,
                    yearObj,
                    today,
                    isFirstPage,
                    SourceNames
                );

                isFirstPage = false;
            }

            doc.save("Property_Source_Yearly_Report.pdf");
        }
    };

    const handleClear = () => {
        setSelectedProperty([]);
        setSelectedYear([]);
        setSelectedSource([]);
    };

    return (
        <>
            {loading && (
                <div className="fullscreen-loader">
                    <div className="fas fa-spinner fa-spin fa-3x"></div>
                </div>
            )}

            <div className="wrapper">
                <ReservationSidebar />
                <div className="main-panel">
                    <ReservationHeader />

                    <div className="container">
                        <div className="page-inner">
                            <h3 className='fw-bold'>
                                {reportType === "source"
                                    ? "Source Yearly Report"
                                    : "Property Source Yearly Report"}
                            </h3>

                            <form>
                                <div className='row mb-3'>
                                    <div className='col-md-12'>
                                        <label>Property</label>
                                        <Select
                                            options={propertyOptions}
                                            isMulti
                                            value={selectedProperty}
                                            onChange={handlePropertyChange}
                                        />
                                    </div>
                                </div>

                                <div className='row mb-3'>
                                    <div className='col-md-4'>
                                        <label>Year</label>
                                        <Select
                                            options={yearOptions}
                                            isMulti
                                            value={selectedYear}
                                            onChange={handleYearChange}
                                        />
                                    </div>

                                    <div className='col-md-4'>
                                        <label>Source</label>
                                        <Select
                                            options={sourceOptions}
                                            isMulti
                                            value={selectedSource}
                                            onChange={handleSourceChange}
                                        />
                                    </div>

                                    <div className='col-md-2'>
                                        <div style={{ marginTop: "30px" }}>

                                            <input
                                                type='radio'
                                                className='me-2'
                                                name="reportType"
                                                checked={reportType === "source"}
                                                onChange={() => setReportType("source")}
                                            />
                                            <label className='me-3'>Source</label>

                                            <input
                                                type='radio'
                                                className='me-2'
                                                name="reportType"
                                                checked={reportType === "property"}
                                                onChange={() => setReportType("property")}
                                            />
                                            <label>Property</label>

                                        </div>
                                    </div>

                                    <div className='col-md-2 d-flex align-items-end'>
                                        <button onClick={handleReport} className='btn btn-primary me-2'>
                                            Report
                                        </button>

                                        <button type="button" className='btn btn-danger' onClick={handleClear}>
                                            Clear
                                        </button>
                                    </div>
                                </div>
                            </form>

                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}